import React, { useRef, useState, useEffect } from 'react';

export interface ChartContainerFit {
  containerWidth: number;
  containerHeight: number;
  optimalDimension: number;
  scaleFactor: number;
}

interface ResponsiveChartContainerProps {
  children: (dimensions: ChartContainerFit) => React.ReactNode;
  /** Maximum readable dimension cap in pixels (default 720px) */
  maxDimension?: number;
  /** Minimum dimension in pixels to ensure readability (default 240px) */
  minDimension?: number;
  /** Aspect ratio to enforce (default 1.0 for square astrological charts) */
  aspectRatio?: number;
  /** Additional custom class */
  className?: string;
  /** Force full width expansion up to maxDimension */
  fillAvailable?: boolean;
}

/**
 * ResponsiveChartContainer
 * 
 * Dynamically computes available container width and height using ResizeObserver.
 * Calculates optimal aspect-ratio-preserving square chart rectangle.
 * Maximizes readable chart area without distorting Vedic chart geometry or cropping boundaries.
 */
export const ResponsiveChartContainer: React.FC<ResponsiveChartContainerProps> = ({
  children,
  maxDimension = 760,
  minDimension = 220,
  aspectRatio = 1.0,
  className = '',
  fillAvailable = true
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<ChartContainerFit>({
    containerWidth: 400,
    containerHeight: 400,
    optimalDimension: 400,
    scaleFactor: 1.0
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateDimensions = () => {
      const rect = el.getBoundingClientRect();
      const availableW = Math.max(minDimension, rect.width);
      // If height is 0 or unconstrained, use available width or viewport heuristic
      const availableH = rect.height > 50 ? rect.height : availableW;

      // Determine dimension based on aspect ratio
      const limiting = Math.min(availableW, availableH * aspectRatio);
      const capped = Math.min(maxDimension, limiting);
      const optimalDimension = Math.max(minDimension, capped);

      setFit({
        containerWidth: availableW,
        containerHeight: availableH,
        optimalDimension,
        scaleFactor: optimalDimension / 400
      });
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to prevent resize loop notifications
      window.requestAnimationFrame(updateDimensions);
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, [maxDimension, minDimension, aspectRatio]);

  return (
    <div
      ref={containerRef}
      className={`w-full min-w-0 max-w-full flex items-center justify-center relative overflow-hidden ${className}`}
      style={{
        minHeight: `${minDimension}px`
      }}
    >
      <div
        className="aspect-square w-full min-w-0 flex items-center justify-center transition-all duration-150"
        style={{
          maxWidth: `${fit.optimalDimension}px`,
          maxHeight: `${fit.optimalDimension}px`,
          width: '100%',
          aspectRatio: '1 / 1'
        }}
      >
        {children(fit)}
      </div>
    </div>
  );
};
